import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { IgButton } from "@intelligems/ig-theme";
import { ExperienceDto } from "@intelligems/ig-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useStartExperience } from "../../../../api/experience-actions/start-experience";
import { isDialogOpen, setDialog } from "../../../../store/slices/global";
import { intelligemsRateShippingTest } from "../../../../utils/linkSheet";
import openNewTab from "../../../../utils/openNewTab";

const PREFIX = "ShippingRatesDialog";

const classes = {
  dialogTitle: `${PREFIX}-dialogTitle`,
  articleLink: `${PREFIX}-articleLink`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
  },
  [`& .${classes.articleLink}`]: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
}));

interface ShippingRatesDialogProps {
  currExperience?: ExperienceDto;
}

const startExpText =
  "If you choose to manually update your shipping rates you will be responsible for updating your shopify shipping rates before and after a test. For more information, see our help docs";

export const ShippingRatesDialog = ({
  currExperience,
}: ShippingRatesDialogProps) => {
  const isOpen = useSelector(isDialogOpen("shippingRates"));
  const dispatch = useDispatch();
  const startExperience = useStartExperience();

  const handleShippingStart = () => {
    if (currExperience) {
      startExperience.mutate({
        body: { variationId: undefined },
        pathParams: {
          pausedAtTs: currExperience.pausedAtTs ?? 0,
          name: currExperience!.name!,
          experienceId: currExperience!.id!,
          action: "start",
        },
        queryParams: undefined,
      });
      dispatch(setDialog({ dialog: "shippingRates", open: false }));
    }
  };

  return (
    <StyledDialog
      open={isOpen}
      aria-labelledby="shipping-rates-dialog"
      aria-describedby="shipping-rates-dialog"
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
      maxWidth={"xs"}
      onClose={() => {
        dispatch(setDialog({ dialog: "shippingRates", open: false }));
      }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <DialogTitle
          sx={{ width: "350px" }}
          id="dialogBox-title"
          className={classes.dialogTitle}
        >
          Do you want the Intelligems Wizard to help set your shipping rates?
        </DialogTitle>
        <DialogContent sx={{ width: "350px" }}>
          <Typography>
            {startExpText}
            <strong
              className={classes.articleLink}
              onClick={() => {
                openNewTab(intelligemsRateShippingTest);
              }}
            >
              {" "}
              here.
            </strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Grid container direction={"column"}>
            <Link
              to={{
                pathname: `/shipping-profiles/${currExperience!.id}`,
              }}
              state={{
                id: currExperience!.id,
              }}
              style={{ textDecoration: "none" }}
            >
              <IgButton
                loading={startExperience.isPending}
                variant={"contained"}
              >
                Yes, Update My Rates
              </IgButton>
            </Link>
            <IgButton
              loading={startExperience.isPending}
              onClick={() => {
                handleShippingStart();
              }}
            >
              {}
              No, I'll Update My Rates
            </IgButton>
          </Grid>
        </DialogActions>
      </Grid>
    </StyledDialog>
  );
};
